import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Providers } from './providers';
import { CustomerLayout, BottomNav } from '@kss/layouts';
import { CartDrawer, useCart } from '@kss/ui';
import { RequireAuth } from '@kss/auth';

import Home from './pages/Home';
import QrMenu from './pages/QrMenu';
import OrderTrack from './pages/OrderTrack';
import TableEntry from './pages/TableEntry';
import Booking from './pages/Booking';
import Login from './pages/Login';
import StaffLogin from './pages/StaffLogin';
import AccountProfile from './account/AccountProfile';
import RewardsWallet from './account/RewardsWallet';

/** Renders the global cart drawer and wires its checkout CTA to the order flow. */
function CartDrawerHost() {
  const navigate = useNavigate();
  const { closeCart } = useCart();
  return (
    <CartDrawer
      onCheckout={() => {
        closeCart();
        navigate('/order');
      }}
    />
  );
}

export default function App() {
  // Served at the site root in dev; under a sub-path (e.g. /panel is a sibling)
  // in the combined IIS deploy. import.meta.env.BASE_URL reflects the build `base`.
  const baseUrl = (import.meta as unknown as { env: { BASE_URL: string } }).env.BASE_URL;
  const basename = baseUrl.replace(/\/$/, '') || undefined;
  return (
    <Providers>
      <BrowserRouter basename={basename}>
        <Routes>
          {/* Storefront shell (header + footer + bottom nav). Petpooja flow: guests browse
              the home + menu freely; name + mobile (OTP) is asked only at the first order.
              Identity-bound screens (account, rewards, booking, orders, bill) still require
              a signed-in user and send guests to /login. */}
          <Route element={<CustomerLayout />}>
            <Route path='/' element={<Home />} />
            <Route
              path='/rewards'
              element={
                <RequireAuth redirectTo='/login'>
                  <RewardsWallet />
                </RequireAuth>
              }
            />
            <Route
              path='/account'
              element={
                <RequireAuth redirectTo='/login'>
                  <AccountProfile />
                </RequireAuth>
              }
            />
            <Route
              path='/booking'
              element={
                <RequireAuth redirectTo='/login'>
                  <Booking />
                </RequireAuth>
              }
            />
          </Route>

          {/* Auth */}
          <Route path='/login' element={<Login />} />
          <Route path='/staff' element={<StaffLogin />} />

          {/* QR landing — captures the (encrypted) table token, then redirects Home. */}
          <Route path='/t/:code' element={<TableEntry />} />

          {/* Menu for the current table — browsable by guests; adding / placing an order
              triggers the name + mobile (OTP) capture the first time (see QrMenu). */}
          <Route path='/order' element={<QrMenu />} />
          <Route
            path='/order/track'
            element={
              <RequireAuth redirectTo='/login'>
                <OrderTrack />
              </RequireAuth>
            }
          />
          <Route
            path='/order/bill'
            element={
              <RequireAuth redirectTo='/login'>
                <OrderTrack />
              </RequireAuth>
            }
          />

          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
        <CartDrawerHost />
        {/* App-style bottom nav on every screen. */}
        <BottomNav />
      </BrowserRouter>
    </Providers>
  );
}
